// MoffAPI
import axios, { AxiosInstance } from 'axios';
import { Dispatch } from 'redux';
import * as Moff from '../constants/MoffAPI';
import { getAllHospitals, getDoctorsBelongHospital, getHospitalDetails, getDoctorDetails, getAllPatients, getPatientDetails, getInquiryDetails } from '../actions/MoffApi';
import * as AWS from '../constants/AWS';
import { Patient, Doctor, Hospital, Inquiry } from '../interfaces';
import {
  CognitoUserAttribute 
} from 'amazon-cognito-identity-js';

export class MoffAPI {
  /** インスタンス */
  private static _instance: MoffAPI;
  private axiosInstance: AxiosInstance;

  /** インスタンスの取得 */
  public static get instance(): MoffAPI | null {
    // _inctanceが存在しない場合に、new MoffAPI()を実行する。
    if (!this._instance) {
      this._instance = new MoffAPI();

      // .envの設定
      const API_BASE_URL = String(process.env.REACT_APP_MOFF_API_URL);

      const userPool = AWS.userPool();
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser === null) {
        return null;
      }

      const self = this;
      cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          return;
        }

        if (session) {
          self._instance.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            headers: {
              'Authorization': session.getIdToken().getJwtToken()
            }
          });
        }
      });
    }

    // 生成済みのインスタンスを返す
    return this._instance;
  }

  // Redux.
  // ログインユーザ名
  public requestGetUserId = (requestFunc: Function, fetchedFunc: Function) => {
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());

      const userPool = AWS.userPool();
      const cognitoUser = userPool.getCurrentUser();
      console.log(cognitoUser);

      if (cognitoUser != null) {
        new Promise<
            CognitoUserAttribute[]
        >(async (resolve, reject) => {
          await cognitoUser.getUserAttributes((err, a: CognitoUserAttribute[]) => {
              if (err) {
                  reject(err);
              } else {
                  resolve(a);
              }
          });
        }).then( async attr => {
          const userId = await attr
              .filter(v => v.getName() === 'custom:userId')
              .map(v => v.getValue())
              .find(() => true);
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_USER_ID_SUCCESS, userId));
        }).catch( error => {
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
      } else {
        dispatch(fetchedFunc(Moff.ERROR, 'cannot get UserID. It may that user is not signed-in'));
      }

    };
  }

  // ログインユーザー名
  public requestGetUserName = (requestFunc: Function, fetchedFunc: Function) => {
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());

      const userPool = AWS.userPool();
      const cognitoUser = userPool.getCurrentUser();
      console.log(cognitoUser);

      if (cognitoUser != null) {
        const userName = cognitoUser.getUsername();
        dispatch(fetchedFunc(Moff.LOADING, null));
        dispatch(fetchedFunc(Moff.FETCH_USER_NAME_SUCCESS, userName));
      } else {
        dispatch(fetchedFunc(Moff.ERROR, 'User is not signed-in'));
      }
    };
  }

  // 利用者一覧取得(複数)
  /**
   *
   *
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getAllPatients(requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('patients')
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_PATIENTS_SUCCESS, response.data));
          dispatch(getAllHospitals());
        })
        .catch((error: any) => {
          self.showAxiosError('getAllPatients', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getAllDoctors(requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('doctors')
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_DOCTORS_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getAllDoctors', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {string} hospitalId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getDoctorsBelongHospital(hospitalId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('hospitals/' + hospitalId + '/doctors')
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_DOCTORS_IN_HOSPITAL_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getDoctorsBelongHospital', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {string} patientId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getPatientsBelongHospital(patientId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('hospitals/' + patientId + '/patients')
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_PATIENTS_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getPatientsBelongHospital', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  public getDoctorDetailsBelongHospital(hospitalId: string, doctorId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('hospitals/' + hospitalId + '/doctors/' + doctorId)
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_DOCTOR_DETAILS_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getDoctorDetailsBelongHospital', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getAllHospitals(requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('hospitals')
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_HOSPITALS_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getAllHospitals', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {string} patientId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getPatientDetails(patientId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('patients/' + patientId)
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_PATIENT_DETAILS_SUCCESS, response.data));
          dispatch(getAllHospitals());
        })
        .catch((error: any) => {
          self.showAxiosError('getPatientDetails', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {string} hospitalId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getHospitalDetails(hospitalId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('hospitals/' + hospitalId)
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_HOSPITAL_DETAILS_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getHospitalDetails', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {string} patientId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getPatientDayTrainingLogs(patientId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('patients/' + patientId + '/dayTrainingLogs')
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_PATIENT_DAYTRAININGLOGS_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getPatientDayTrainingLogs', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   *
   * @param {Array<any>} patientIds
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getPatientsDayTrainingLogs(patientIds: any, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return async (dispatch: Dispatch<any>) => {
      let data = [];
      dispatch(requestFunc());
      dispatch(fetchedFunc(Moff.LOADING, null));
      if (patientIds) {
        for (const id of patientIds) {
          await this.axiosInstance.get('patients/' + id + '/dayTrainingLogs')
            .then((response: any) => {
              data.push(response.data);
            })
            .catch((error: any) => {
              self.showAxiosError('getPatientDayTrainingLogs', error);
              dispatch(fetchedFunc(Moff.ERROR, error));
            });
        }
        dispatch(fetchedFunc(Moff.FETCH_PATIENTS_DAYTRAININGLOGS_SUCCESS, data));
      }
    };
  }

  /**
   *
   *
   * @param {string} doctorId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getDoctorDetails(doctorId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('doctors/' + doctorId)
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_DOCTOR_DETAILS_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getDoctorDetails', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  // 問い合わせ一覧取得(複数)
  /**
   *
   *
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getAllInquiries(requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('inquiries')
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_INQUIRIES_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getAllInquiries', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  /**
   *
   * @param {string} inquiryId
   * @param {Function} requestFunc
   * @param {Function} fetchedFunc
   * @returns {*}
   * @memberof MoffAPI
   */
  public getInquiryDetails(inquiryId: string, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.get('inquiries/details/' + inquiryId)
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.LOADING, null));
          dispatch(fetchedFunc(Moff.FETCH_INQUIRY_SUCCESS, response.data));
        })
        .catch((error: any) => {
          self.showAxiosError('getInquiryDetails', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  public postAddPatient(
    params: Patient, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.post('patients', params)
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.ADD_PATIENT_SUCCESS, response.data));
          dispatch(getAllPatients());
        })
        .catch((error: any) => {
          self.showAxiosError('postAddPatient', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  public postAddDoctor(
    params: Doctor, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.post(`hospitals/${params.hospitalId}/doctors`, params)
        .then((response: any) => {
          dispatch(fetchedFunc(Moff.ADD_DOCTOR_SUCCESS, response.data));
          dispatch(getDoctorsBelongHospital(params.hospitalId));
        })
        .catch((error: any) => {
          self.showAxiosError('postAddDoctor', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  public postAddHospital(
    params: Hospital, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.post('hospitals', params)
        .then(async (response: any) => {
          await dispatch(fetchedFunc(Moff.ADD_HOSPITAL_SUCCESS, response.data));
          dispatch(getAllHospitals());
        })
        .catch((error: any) => {
          self.showAxiosError('postAddHospital', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  public putUpdateHospital(
    params: Hospital, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.put(`hospitals/${params.id}`, params)
        .then(async (response: any) => {
          await dispatch(fetchedFunc(Moff.UPDATE_HOSPITAL_SUCCESS, response.data));
          dispatch(getHospitalDetails(params.id));
        })
        .catch((error: any) => {
          self.showAxiosError('putUpdateHospital', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }

  public putUpdateDoctor(
    params: Doctor, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.put(`doctors/${params.id}`, params)
        .then(async (response: any) => {
          await dispatch(fetchedFunc(Moff.UPDATE_DOCTOR_SUCCESS, response.data));
          dispatch(getDoctorDetails(params.id));
        })
        .catch((error: any) => {
          self.showAxiosError('putUpdateDoctor', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }
  public putUpdatePatient(
    params: Patient, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.put(`patients/${params.id}`, params)
        .then(async (response: any) => {
          await dispatch(fetchedFunc(Moff.UPDATE_PATIENT_SUCCESS, response.data));
          dispatch(getPatientDetails(params.id));
        })
        .catch((error: any) => {
          self.showAxiosError('putUpdatePatient', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }
  public putUpdateInquiry(
    params: Inquiry, requestFunc: Function, fetchedFunc: Function): any {
    const self = this;
    return (dispatch: Dispatch<any>) => {
      dispatch(requestFunc());
      this.axiosInstance.put(`inquiries/details/${params.inquiryId}`, params)
        .then(async (response: any) => {
          await dispatch(fetchedFunc(Moff.UPDATE_INQUIRY_SUCCESS, response.data));
          dispatch(getInquiryDetails(params.inquiryId));
        })
        .catch((error: any) => {
          self.showAxiosError('putUpdateInquiry', error);
          dispatch(fetchedFunc(Moff.ERROR, error));
        });
    };
  }
  /** プライベートコンストラクタ */
  private constructor() {
  }

  // エラーポップアップ.
  // apiNameはどのAPIによるものか.
  private showAxiosError(apiName: string, error: any) {
    if (error.toString().indexOf('Network Error') !== -1) {
      const errorMessage = '通信エラーが発生しました。インターネット接続が出来ることを確認してから再度ページを読み込み直して下さい。';
      alert(errorMessage);
    } else if (error.toString().indexOf('timeout') !== -1) {
      const errorMessage = '通信エラーが発生しました。インターネット接続が良好な環境で、再度ページを読み込み直して下さい。';
      alert(errorMessage);
    }

    // ログインへ.
    // window.location.href = '/';
  }
}



// WEBPACK FOOTER //
// ./src/api/MoffApi.tsx